/**
 * TenantAdmins — list users carrying the `tenant_admin` role across tenants.
 *
 * Super-admin: cross-tenant view with inline filters (search by email, scope
 * to a tenant), sortable columns (Name, Email, Tenant, Joined), and a Tenant
 * column showing the resolved tenant name. Promote / Invite require a
 * specific tenant to be selected in the inline filter so the action knows
 * where to write.
 *
 * Tenant-admin: scoped to their own tenant (Tenant column hidden).
 *
 * Also embeddable: given a `tenantId`, it locks to that tenant and drops its own
 * heading, which is how the tenant detail page shows a tenant's administrators
 * without a second implementation drifting from this one.
 *
 * Backend:
 *   GET  /console/users?role=tenantAdmin[&search=&tenantId=&sortBy=&sortOrder=]
 *   PATCH /console/users/{id}                   ← promote/demote (sets roles[])
 *
 * See docs/FeatureDesigns/multi-tenancy/multi-tenancy-rollout-plan.md §T1 step 3b.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    DataTable,
    SearchFilterBar,
    type ColumnDef,
    type FilterDef,
    type SortState,
    Button,
    Modal,
    FormField,
    Input,
    Select,
    formatDateShort,
} from '@shared-ui/components';
import { userApi } from '../../../api/userApi';
import { tenantsApi } from '../../../api/tenantsApi';
import { authApi } from '../../../api/auth';
import { useSelectedTenantId, hasScopedTenant } from '../../../components/TenantFilter';
import type { User } from '../../../types';

export default function TenantAdmins({ tenantId, embedded }: { tenantId?: string; embedded?: boolean } = {}) {
    const queryClient = useQueryClient();
    const me = authApi.getUser();
    const isSuperAdmin = me?.isSuperAdmin === true;

    const [error, setError] = useState('');
    const [promoteOpen, setPromoteOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);

    // ── Filters & sort ─────────────────────────────────────────────────
    // Tenant scope comes from the sidebar TenantFilter (the global control
    // shared with every tenant-aware page). Email search is inline. Tenant-
    // admin users are locked to their own tenant — they don't see the
    // sidebar filter at all.
    const sidebarTenantId = useSelectedTenantId();
    const [filters, setFilters] = useState<Record<string, string>>({ search: '' });
    const [sort, setSort] = useState<SortState>({ sortBy: 'createdAt', sortOrder: 'desc' });
    const emailSearch = filters.search ?? '';

    // Names for the Tenant column (super-admin cross-tenant view).
    // /platform/tenants is super-admin only, so tenant-admins skip this and
    // fetch just their own tenant's details via /me/tenant below.
    const tenantsQuery = useQuery({
        queryKey: ['tenants-for-tenant-admins'],
        queryFn: () => tenantsApi.list(),
        staleTime: 60_000,
        enabled: isSuperAdmin,
    });
    const tenantNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of tenantsQuery.data?.tenants ?? []) {
            map.set(t.id, t.name || t.id);
        }
        return map;
    }, [tenantsQuery.data]);

    // Tenant-admin's own tenant info (for the header label).
    const myTenantQuery = useQuery({
        queryKey: ['my-tenant'],
        queryFn: () => tenantsApi.getMine(),
        staleTime: 60_000,
        enabled: !isSuperAdmin,
    });

    // Tenant-admin: locked to own tenant. Super-admin: honors the sidebar
    // TenantFilter — `hasScopedTenant` is true only when a real tenant is
    // selected (not "All tenants" / "Platform").
    // An explicit `tenantId` wins over both: the embedding page is already about
    // one tenant, and reading the sidebar there would let a filter set elsewhere
    // silently change whose administrators are on screen.
    const tenantIdParam: string | undefined = tenantId ?? (isSuperAdmin
        ? (hasScopedTenant(sidebarTenantId) ? sidebarTenantId : undefined)
        : (me?.tenantId ?? '0'));

    const listQuery = useQuery({
        queryKey: ['tenant-admins', tenantIdParam ?? '_all_', emailSearch, sort.sortBy, sort.sortOrder],
        queryFn: () => userApi.getUsers({
            role: 'tenantAdmin',
            search: emailSearch || undefined,
            tenantId: tenantIdParam,
            sortBy: sort.sortBy,
            sortOrder: sort.sortOrder,
            pageSize: 100,
        }),
    });

    const demoteMut = useMutation({
        // Server-side: this clears admin-assignable roles only; app roles
        // (student/teacher) are preserved.
        mutationFn: (userId: string) => userApi.updateUser(userId, { roles: [] }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant-admins'] });
            setError('');
        },
        onError: (err: Error) => setError(err.message),
    });

    const tenantAdmins = listQuery.data?.items ?? [];

    const columns: ColumnDef<User>[] = useMemo(() => {
        const cols: ColumnDef<User>[] = [
            { key: 'name',  header: 'Name',  sortable: true, render: u => u.name || '—' },
            { key: 'email', header: 'Email', sortable: true, render: u => u.email },
        ];
        if (isSuperAdmin) {
            cols.push({
                key: 'tenantId',
                header: 'Tenant',
                sortable: true,
                render: u => {
                    const id = u.tenantId ?? '0';
                    const name = tenantNameById.get(id);
                    return name ? `${name}` : id;
                },
            });
        }
        cols.push(
            {
                key: 'roles',
                header: 'Roles',
                render: u => (u.roles && u.roles.length > 0
                    ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.roles.join(', ')}</span>
                    : '—'),
            },
            {
                key: 'createdAt',
                header: 'Joined',
                sortable: true,
                render: u => u.createdAt ? formatDateShort(u.createdAt) : '—',
            },
            {
                key: 'actions',
                header: '',
                render: u => (
                    <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                            if (confirm(`Remove "${u.name || u.email}" as tenant admin?`)) {
                                demoteMut.mutate(u.id);
                            }
                        }}
                        loading={demoteMut.isPending && demoteMut.variables === u.id}
                    >
                        Demote
                    </Button>
                ),
            },
        );
        return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demoteMut.isPending, demoteMut.variables, isSuperAdmin, tenantNameById]);

    // The tenant whose admins are on screen — sidebar selection for a
    // super-admin, own tenant for a tenant-admin. Empty for "All tenants" /
    // "Platform", where the page really is cross-tenant. `tenantNameById` is
    // only populated for a super-admin (the /platform/tenants query is theirs
    // alone), so a tenant-admin reads its name off /me/tenant instead.
    const scopedTenantName: string = tenantId
        ? (tenantNameById.get(tenantId) ?? tenantId)
        : (isSuperAdmin
            ? (hasScopedTenant(sidebarTenantId) ? (tenantNameById.get(sidebarTenantId) ?? sidebarTenantId) : '')
            : (myTenantQuery.data?.name || me?.tenantId || ''));

    // Promote / Invite write to a specific tenant. For super-admin, require
    // the sidebar TenantFilter to be set to a real tenant; for tenant-admin,
    // use their own.
    const writeTenantId: string | null = tenantId ?? (isSuperAdmin
        ? (hasScopedTenant(sidebarTenantId) ? sidebarTenantId : null)
        : (me?.tenantId ?? '0'));
    const canWrite = writeTenantId !== null;
    const writeBlockedHint = 'Pick a specific tenant in the sidebar filter to enable this action.';

    return (
        <div className="tenant-admins-page" style={{ padding: embedded ? 0 : 'var(--spacing-lg)' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
                <div>
                    {/* Embedded, the page it sits in already names the tenant — a second
                        "<Tenant> Tenant Admins" heading under its own title reads as a
                        different page having been opened. */}
                    {!embedded && (
                        <h1 style={{ margin: 0 }}>
                            {scopedTenantName ? `${scopedTenantName} Tenant Admins` : 'Tenant Admins'}
                        </h1>
                    )}
                    <p style={{ margin: embedded ? 0 : '4px 0 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {scopedTenantName
                            ? 'All users carrying the tenant_admin role in this tenant.'
                            : 'All users carrying the tenant_admin role across tenants.'}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                    <Button
                        variant="outline"
                        onClick={() => { setPromoteOpen(true); setError(''); }}
                        disabled={!canWrite}
                        title={canWrite ? undefined : writeBlockedHint}
                    >
                        + Promote existing user
                    </Button>
                    <Button
                        onClick={() => { setInviteOpen(true); setError(''); }}
                        disabled={!canWrite}
                        title={canWrite ? undefined : writeBlockedHint}
                    >
                        + Invite by email
                    </Button>
                </div>
            </header>

            {/* Inline email search — super-admin only. Tenant scope comes
                from the sidebar TenantFilter, not an inline control. */}
            {isSuperAdmin && (
                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                    <SearchFilterBar
                        filters={[] satisfies FilterDef[]}
                        values={filters}
                        onChange={setFilters}
                        searchPlaceholder="Filter by name or email"
                        hideFetch
                    />
                </div>
            )}

            {error && (
                <div style={{ padding: 'var(--spacing-sm)', background: 'var(--status-error-bg)', color: 'var(--status-error-text)', borderRadius: 4, marginBottom: 'var(--spacing-md)' }}>
                    {error}
                </div>
            )}

            <DataTable
                columns={columns}
                data={tenantAdmins}
                rowKey={u => u.id}
                loading={listQuery.isLoading}
                emptyMessage={
                    emailSearch
                        ? 'No matching tenant admins.'
                        : tenantId
                            ? 'Nobody administers this tenant yet. Promote an existing user, or invite one by email.'
                            : isSuperAdmin && !hasScopedTenant(sidebarTenantId)
                                ? 'No users carry the tenant_admin role yet.'
                                : 'No matching tenant admins.'
                }
                bordered
                sort={sort}
                onSortChange={setSort}
            />

            {canWrite && (
                <>
                    <PromoteModal
                        isOpen={promoteOpen}
                        tenantId={writeTenantId!}
                        tenantName={scopedTenantName}
                        onClose={() => setPromoteOpen(false)}
                        onPromoted={() => {
                            queryClient.invalidateQueries({ queryKey: ['tenant-admins'] });
                            setPromoteOpen(false);
                        }}
                        onError={setError}
                    />

                    <InviteModal
                        isOpen={inviteOpen}
                        tenantId={writeTenantId!}
                        tenantName={scopedTenantName}
                        onClose={() => setInviteOpen(false)}
                        onInvited={() => {
                            queryClient.invalidateQueries({ queryKey: ['tenant-admins'] });
                            setInviteOpen(false);
                        }}
                        onError={setError}
                    />
                </>
            )}
        </div>
    );
}

// ── Promote modal — find an existing user in the tenant + flip their role ──

function PromoteModal(props: {
    isOpen: boolean;
    tenantId: string;
    tenantName: string;
    onClose: () => void;
    onPromoted: () => void;
    onError: (msg: string) => void;
}) {
    const [search, setSearch] = useState('');
    const [submittingId, setSubmittingId] = useState<string | null>(null);

    const candidatesQuery = useQuery({
        queryKey: ['tenant-admin-candidates', props.tenantId, search],
        queryFn: () => userApi.getUsers({
            role: 'any', // type-agnostic — student / teacher / admin are all promotable
            tenantId: props.tenantId,
            search: search || undefined,
            pageSize: 20,
        }),
        enabled: props.isOpen,
    });

    const promoteMut = useMutation({
        mutationFn: (userId: string) => userApi.updateUser(userId, { roles: ['tenant_admin'] }),
        onSuccess: () => {
            setSubmittingId(null);
            setSearch('');
            props.onPromoted();
        },
        onError: (err: Error) => {
            setSubmittingId(null);
            props.onError(err.message);
        },
    });

    const candidates = (candidatesQuery.data?.items ?? [])
        .filter(u => !u.roles?.includes('tenant_admin'));

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={`Promote user to tenant admin — ${props.tenantName}`}
            size="md"
            footer={<Button variant="outline" onClick={props.onClose}>Close</Button>}
        >
            <FormField label="Search users in this tenant">
                <Input
                    type="text"
                    autoFocus
                    placeholder="name or email"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </FormField>

            {candidatesQuery.isLoading && <div style={{ padding: 'var(--spacing-sm)' }}>Loading…</div>}

            {!candidatesQuery.isLoading && candidates.length === 0 && (
                <div style={{ padding: 'var(--spacing-sm)', color: 'var(--text-secondary)' }}>
                    No users found in tenant "{props.tenantName}". Add the user to the tenant first
                    (set their tenantId via the platform Tenants page or the Users page).
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 'var(--spacing-sm)' }}>
                {candidates.map(u => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--spacing-xs) var(--spacing-sm)', borderRadius: 4, background: 'var(--bg-secondary)' }}>
                        <div>
                            <div style={{ fontWeight: 500 }}>{u.name || '—'}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{u.email}</div>
                        </div>
                        <Button
                            size="sm"
                            onClick={() => { setSubmittingId(u.id); promoteMut.mutate(u.id); }}
                            loading={submittingId === u.id}
                        >
                            Promote
                        </Button>
                    </div>
                ))}
            </div>
        </Modal>
    );
}

// ── Invite modal — invite a new user (or add an existing one) by email ──

function InviteModal(props: {
    isOpen: boolean;
    tenantId: string;
    tenantName: string;
    onClose: () => void;
    onInvited: () => void;
    onError: (msg: string) => void;
}) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    // Single role dropdown — every assignable role except super_admin.
    // The backend's `InvitableRoleSchema` enforces the same set.
    type InviteRole =
        | 'student' | 'teacher' | 'tenant_admin'
        | 'content_admin' | 'prompt_admin' | 'eval_admin' | 'user_admin';
    const [role, setRole] = useState<InviteRole>('student');
    const [result, setResult] = useState<{ isNewUser: boolean; emailSent: boolean } | null>(null);

    const inviteMut = useMutation({
        mutationFn: () => userApi.inviteTenantMember({
            email: email.trim(),
            name: name.trim() || undefined,
            tenantId: props.tenantId,
            role,
        }),
        onSuccess: (res) => {
            // Keep the modal open so the admin sees the success state; the
            // list refreshes on Done via handleClose → props.onInvited.
            setResult({ isNewUser: res.isNewUser, emailSent: res.emailSent });
        },
        onError: (err: Error) => props.onError(err.message),
    });

    function handleClose() {
        if (result) props.onInvited();
        else props.onClose();
        setEmail(''); setName(''); setRole('student'); setResult(null);
    }

    function handleSubmit() {
        if (!email.trim()) return;
        setResult(null);
        inviteMut.mutate();
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={handleClose}
            title={`Invite to "${props.tenantName}"`}
            size="md"
            footer={
                result ? (
                    <Button onClick={handleClose}>Done</Button>
                ) : (
                    <>
                        <Button variant="outline" onClick={handleClose}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            loading={inviteMut.isPending}
                            disabled={!email.trim() || inviteMut.isPending}
                        >
                            Send invite
                        </Button>
                    </>
                )
            }
        >
            {!result && (
                <>
                    <FormField label="Email" required>
                        <Input
                            type="email"
                            autoFocus
                            placeholder="user@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </FormField>

                    <FormField label="Name (optional)">
                        <Input
                            type="text"
                            placeholder="Leave blank to use the email prefix"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </FormField>

                    <FormField label="Role" required>
                        <Select
                            value={role}
                            onChange={e => setRole(e.target.value as InviteRole)}
                        >
                            <option value="student">Student</option>
                            <option value="teacher">Teacher</option>
                            <option value="tenant_admin">Tenant admin</option>
                            <option value="content_admin">Content admin</option>
                            <option value="prompt_admin">Prompt admin</option>
                            <option value="eval_admin">Eval admin</option>
                            <option value="user_admin">User admin</option>
                        </Select>
                    </FormField>

                    <div style={{ marginTop: 'var(--spacing-sm)', fontSize: 12, color: 'var(--text-tertiary)' }}>
                        If the email is already a BuzzBrain user they'll be added to this tenant directly
                        and the chosen role is appended to their existing roles. Otherwise, a pending
                        account is created and a sign-up email is sent — the link points to the
                        student / teacher / admin app matching the role.
                    </div>
                </>
            )}

            {result && (
                <div style={{ padding: 'var(--spacing-sm)', background: 'var(--status-success-bg)', color: 'var(--status-success-text)', borderRadius: 4 }}>
                    {result.isNewUser
                        ? 'Pending account created.'
                        : 'Existing user added to the tenant.'}
                    {' '}
                    {result.emailSent
                        ? 'Invitation email sent.'
                        : 'Email delivery failed — please share the BuzzBrain link manually.'}
                </div>
            )}
        </Modal>
    );
}
